import type { NodeRole, PrimitiveValueName, ValueNameToType } from "@skilitics/nautilus-core";
import { toReferencedValue } from "@skilitics/nautilus-core-designer-data";

import { defaultValueForConfig } from "../defaultValueForConfig.ts";
import type { ResolveDesignTimeValue } from "../resolveDesignTimeValue.ts";
import type { ValueSubtype } from "../valueSubtypes.ts";
import type { ResolveAvailableValues } from "./resolveAvailableValues.ts";

/** A value narrowed to its runtime plane — the shape a binding target needs. */
export type AvailableBindableTarget<TName extends PrimitiveValueName = PrimitiveValueName> = {
  nodeId: number;
  nodeRole?: NodeRole;
  title: string;
  description?: string;
  type: TName;
  /** Carried through so effect and binding target pickers filter identically. */
  subtype?: ValueSubtype;
  property: string;
  fallback: ValueNameToType<TName>;
};

interface CreateOptions {
  resolveAvailableValues: ResolveAvailableValues;
  resolveDesignTimeValue: ResolveDesignTimeValue;
}

/** Available values narrowed to those with a runtime plane — i.e. usable as live binding targets. */
export const resolveAvailableBindableTargetsCreate =
  ({ resolveAvailableValues, resolveDesignTimeValue }: CreateOptions) =>
  (contextNodeId: number): AvailableBindableTarget[] =>
    resolveAvailableValues(contextNodeId).flatMap(
      ({ nodeId, nodeRole, name, type, subtype, title, description, designTime, runtimePath }) =>
        runtimePath
          ? [
              {
                nodeId,
                nodeRole,
                title,
                description,
                type,
                subtype,
                property: runtimePath,
                fallback:
                  (designTime ? resolveDesignTimeValue(toReferencedValue({ nodeId, name })) : undefined) ??
                  defaultValueForConfig(type),
              },
            ]
          : [],
    );

export type ResolveAvailableBindableTargets = ReturnType<typeof resolveAvailableBindableTargetsCreate>;
